'use client';

import Tooltip from '~/components/Tooltip';
import { cn } from '~/lib/utils';
import { type THeroIcon } from '~/@types/interface';

type FieldRowProps = {
  label: React.ReactNode;
  value: React.ReactNode;
  icon?: THeroIcon;
  hint?: React.ReactNode;
  className?: string;
};

export default function FieldRow({ label, value, icon: Icon, hint, className }: FieldRowProps) {
  return (
    <div
      className={cn(
        'hover:bg-muted/50 flex h-10 items-center gap-3 rounded-md px-1.5 transition-colors',
        className,
      )}
    >
      <div className="text-muted-foreground flex min-w-0 w-36 shrink-0 items-center gap-1.5">
        {Icon ? <Icon className="size-3.5 shrink-0 opacity-80" strokeWidth={2} /> : null}
        <span className="min-w-0 truncate text-sm">{label}</span>
        {hint ? (
          <div className="inline-flex shrink-0">
            <Tooltip
              content={hint}
              hideIcon={false}
              delay={200}
              anchorClassName="inline-flex"
              tooltipClassName="whitespace-normal"
              popoverProps={{
                // Drawer scrolls; portal so tips aren't clipped. Not a native <dialog>.
                disablePortal: false,
                anchorOrigin: { horizontal: 'center', vertical: 'top' },
                transformOrigin: { horizontal: 'center', vertical: 'bottom' },
                slotProps: {
                  root: {
                    // MUI Popover's fixed modal root covers the drawer. Let pointer
                    // events pass through it so hovering the anchor remains stable.
                    style: { position: 'fixed', pointerEvents: 'none' },
                  },
                  paper: {
                    // Keep the tooltip itself hoverable while its modal root is inert.
                    style: { pointerEvents: 'auto' },
                  },
                },
              }}
              iconProps={{ className: 'text-muted-foreground ml-0 size-3.5', height: 14 }}
            >
              <span className="sr-only">{typeof label === 'string' ? label : 'Field'} help</span>
            </Tooltip>
          </div>
        ) : null}
      </div>
      <div className="text-foreground flex h-9 min-w-0 flex-1 items-center text-sm font-medium">
        {value}
      </div>
    </div>
  );
}
